<template>
  <card title="Widget">
    {{ clinic.name.en }}

    <div class="widget-top-actions">
      <el-button
        type="warning"
        :disabled="isParsing"
        @click="handleParseWidget"
      >
        Parse widget
      </el-button>
    </div>

    <div class="widget-form">
      <div class="widget-grid widget-grid--header">
        <div>Field</div>
        <div>Type</div>
        <div>Value</div>
        <div>Description</div>
      </div>

      <div class="widget-grid">
        <div class="widget-grid__label">Active</div>
        <div>toggle</div>
        <div>
          <el-switch v-model="widgetData.widget.is_active" />
        </div>
        <div>Enable or disable widget</div>
      </div>

      <div class="widget-grid">
        <div class="widget-grid__label">Title</div>
        <div>text</div>
        <div>
          <el-input v-model="widgetData.widget.title" />
        </div>
        <div>Widget title</div>
      </div>

      <div class="widget-grid">
        <div class="widget-grid__label">Link</div>
        <div>url</div>
        <div>
          <el-input v-model="widgetData.widget.link" />
        </div>
        <div>Link to clinic page</div>
      </div>

      <div class="widget-grid">
        <div class="widget-grid__label">Sources</div>
        <div>multi select</div>
        <div>
          <el-select
            v-model="widgetData.widget.sources"
            multiple
            collapse-tags
            placeholder="Select sources"
          >
            <el-option
              v-for="source in sourceOptions"
              :key="source.value"
              :label="source.label"
              :value="source.value"
            />
          </el-select>
        </div>
        <div>Review sources displayed in widget</div>
      </div>

      <div class="widget-grid">
        <div class="widget-grid__label">Width</div>
        <div>px / auto</div>
        <div class="widget-grid__inline">
          <el-input v-model="widgetData.styles.max_width" />
          <el-button size="mini" @click="widgetData.styles.max_width = 'auto'">
            auto
          </el-button>
        </div>
        <div>Maximum widget width</div>
      </div>

      <div class="widget-grid">
        <div class="widget-grid__label">Background color</div>
        <div>color</div>
        <div class="widget-grid__color">
          <el-color-picker v-model="widgetData.styles.color_bg" />
          <span class="widget-code">{{ widgetData.styles.color_bg }}</span>
        </div>
        <div>Widget background color</div>
      </div>

      <div class="widget-grid">
        <div class="widget-grid__label">Card color</div>
        <div>color</div>
        <div class="widget-grid__color">
          <el-color-picker v-model="widgetData.styles.color_card" />
          <span class="widget-code">{{ widgetData.styles.color_card }}</span>
        </div>
        <div>Review card background</div>
      </div>

      <div class="widget-grid">
        <div class="widget-grid__label">Text color</div>
        <div>color</div>
        <div class="widget-grid__color">
          <el-color-picker v-model="widgetData.styles.color_text" />
          <span class="widget-code">{{ widgetData.styles.color_text }}</span>
        </div>
        <div>Main text color</div>
      </div>

      <div class="widget-grid">
        <div class="widget-grid__label">Title color</div>
        <div>color</div>
        <div class="widget-grid__color">
          <el-color-picker v-model="widgetData.styles.title_color" />
          <span class="widget-code">{{ widgetData.styles.title_color }}</span>
        </div>
        <div>Widget title color</div>
      </div>

      <div class="widget-grid">
        <div class="widget-grid__label">Muted text color</div>
        <div>color</div>
        <div class="widget-grid__color">
          <el-color-picker v-model="widgetData.styles.color_text_muted" />
          <span class="widget-code">{{ widgetData.styles.color_text_muted }}</span>
        </div>
        <div>Secondary text color</div>
      </div>

      <div class="widget-grid">
        <div class="widget-grid__label">Accent color</div>
        <div>color</div>
        <div class="widget-grid__color">
          <el-color-picker v-model="widgetData.styles.color_primary" />
          <span class="widget-code">{{ widgetData.styles.color_primary }}</span>
        </div>
        <div>Links and active elements color</div>
      </div>

      <div class="widget-grid">
        <div class="widget-grid__label">Border color</div>
        <div>color</div>
        <div class="widget-grid__color">
          <el-color-picker v-model="widgetData.styles.color_border" />
          <span class="widget-code">{{ widgetData.styles.color_border }}</span>
        </div>
        <div>Card border color</div>
      </div>

      <div class="widget-grid">
        <div class="widget-grid__label">Border radius</div>
        <div>px</div>
        <div>
          <el-input v-model="widgetData.styles.border_radius" />
        </div>
        <div>Card corner radius</div>
      </div>

      <div class="widget-grid">
        <div class="widget-grid__label">Container Padding</div>
        <div>px</div>
        <div>
          <el-input v-model="widgetData.styles.container_padding" />
        </div>
        <div>Widget top and bottom padding</div>
      </div>

      <div class="widget-grid">
        <div class="widget-grid__label">Font Family</div>
        <div>select</div>
        <div>
          <el-select v-model="widgetData.styles.font_family" placeholder="Select font">
            <el-option
              v-for="font in fontOptions"
              :key="font.value"
              :label="font.label"
              :value="font.value"
            />
          </el-select>
        </div>
        <div>Widget font family</div>
      </div>
    </div>

    <div class="widget-filters">
      <h3 class="widget-filters__title">Filters</h3>

      <div class="widget-grid widget-grid--header">
        <div>Field</div>
        <div>Type</div>
        <div>Value</div>
        <div>Description</div>
      </div>

      <div class="widget-grid">
        <div class="widget-grid__label">Min rating</div>
        <div>select</div>
        <div>
          <el-select v-model="filters.min_rating">
            <el-option v-for="n in 5" :key="n" :value="n" :label="n" />
          </el-select>
        </div>
        <div>Hide reviews with rating lower than this</div>
      </div>

      <div class="widget-grid">
        <div class="widget-grid__label">Min length</div>
        <div>number</div>
        <div>
          <el-input-number v-model="filters.min_length" :min="1" />
        </div>
        <div>Hide reviews shorter than this</div>
      </div>
    </div>

    <div class="widget-lang">
      <span class="widget-lang__label">Widget language</span>
      <el-select v-model="lang" placeholder="Select language">
        <el-option
          v-for="l in langOptions"
          :key="l.value"
          :label="l.label"
          :value="l.value"
        />
      </el-select>
      <span class="widget-lang__hint">
        Sets data-widget-lang in the embed code. UI labels are translated; reviews appear in this language only if available in the database.
      </span>
    </div>

    <div class="widget-actions">
      <el-button
        type="primary"
        :loading="isSaving"
        @click="handleSave"
      >
        Save settings
      </el-button>

      <el-button
        :loading="isPreviewLoading"
        @click="handlePreview"
      >
        Preview widget
      </el-button>
    </div>

    <div v-if="embedCode" class="widget-snippet">
      <div class="widget-preview__title">Embed code</div>

      <el-input
        type="textarea"
        :rows="12"
        :value="embedCode"
        readonly
      />

      <div class="widget-actions widget-actions--left">
        <el-button size="mini" @click="copyEmbedCode">
          Copy code
        </el-button>
      </div>
    </div>

    <div v-if="previewVisible" :key="previewRenderKey" class="widget-preview">
      <div class="widget-preview__title">Preview</div>
      <div ref="widgetPreviewRoot" class="widget-preview__mount">
        <div id="bookimed-trust-widget" class="widget-preview__widget-root">
          <noscript>
            Powered by
            <a :href="previewLink">Bookimed</a>
          </noscript>
          <div class="bookimed-trust-widget">
            <a
              :href="previewLink"
              class="bookimed-trust-link"
              target="_blank"
              rel="noopener"
              :style="{
                fontSize: '12px',
                color: '#64748B',
                textAlign: 'center',
                display: 'block',
                margin: '8px 0',
                fontFamily: widgetData.styles.font_family || '\'Noto Sans\', sans-serif'
              }"
            >
              Powered by <span class="bookimed-trust-link__brand">Bookimed</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  </card>
</template>

<script>
import {
  getWidgetSettings,
  saveWidgetSettings,
  createWidget
} from '@/api/modules/cms-api-catalogs'

import Analytics from '@/api/modules/analytics'

import './trust-widget.js'
import './trust-widget.css'

const TRUST_WIDGET_API_BASE = 'https://backoffice.bookimed.com/partner-widgets/clinic-widgets/trust'

export default {
  props: ['clinic'],

  data() {
    return {
      isLoading: false,
      isSaving: false,
      isPreviewLoading: false,
      isParsing: false,
      previewData: null,
      embedCode: '',
      widgetId: null,
      previewVisible: false,
      previewRenderKey: 0,

      lang: 'en',
      langOptions: [
        { label: 'English (en)', value: 'en' },
        { label: 'Português BR (pt-BR)', value: 'pt-BR' },
        { label: 'ไทย (th)', value: 'th' },
        { label: 'Русский (ru)', value: 'ru' },
        { label: 'Українська (ua)', value: 'ua' },
        { label: 'العربية (ar)', value: 'ar' },
        { label: 'Қазақша (kz)', value: 'kz' },
        { label: '日本語 (jp)', value: 'jp' },
        { label: 'Italiano (it)', value: 'it' },
        { label: 'Español (es)', value: 'es' },
        { label: 'Bahasa Indonesia (id)', value: 'id' },
        { label: 'Deutsch (de)', value: 'de' },
        { label: 'Français (fr)', value: 'fr' },
        { label: 'Polski (pl)', value: 'pl' }
      ],

      sourceOptions: [
        { label: 'Google', value: 'google' },
        { label: 'Trustpilot', value: 'trustpilot' },
        { label: 'Bookimed', value: 'bookimed' }
      ],

      widgetData: {
        widget: {
          title: '',
          link: '',
          is_active: false,
          sources: ['google', 'trustpilot', 'bookimed']
        },

        styles: {
          max_width: '900px',
          color_bg: '#F8FAFC',
          color_card: '#FFFFFF',
          color_text: '#1E293B',
          color_text_muted: '#64748B',
          color_primary: '#0A7B3F',
          color_border: '#E2E8F0',
          border_radius: '12px',
          title_color: '#1E293B',
          container_padding: '24px',
          font_family: ''
        }
      },

      fontOptions: [
        { label: 'Default', value: '' },
        { label: 'Figtree', value: "'Figtree', sans-serif" },
        { label: 'Noto Sans', value: "'Noto Sans', sans-serif" },
        { label: 'Arial', value: 'Arial, sans-serif' },
        { label: 'Roboto', value: 'Roboto, sans-serif' },
        { label: 'Inter', value: 'Inter, sans-serif' },
        { label: 'Georgia', value: 'Georgia, serif' }
      ],

      filters: {
        min_rating: 3,
        min_length: 5
      }
    }
  },

  computed: {
    previewLink() {
      return this.previewData?.settings?.link || this.widgetData.widget.link || 'https://us-uk.bookimed.com/'
    }
  },

  watch: {
    lang() {
      if (this.embedCode) {
        this.embedCode = this.buildEmbedCode(this.previewData)
      }
      if (this.previewVisible) {
        this.handlePreview()
      }
    }
  },

  mounted() {
    this.loadWidgetSettings()
  },

  methods: {
    setWidgetData(payload = {}) {
      this.widgetData = {
        ...this.widgetData,
        ...payload,
        widget: {
          ...this.widgetData.widget,
          ...(payload.widget || {})
        },
        styles: {
          ...this.widgetData.styles,
          ...(payload.styles || {})
        }
      }

      if (payload.filters) {
        this.filters = {
          ...this.filters,
          ...payload.filters
        }
      }
    },

    mapApiToForm(payload = {}) {
      this.setWidgetData({
        widget: {
          title: payload.title || '',
          link: payload.link || '',
          is_active: Boolean(payload.is_active),
          sources: Array.isArray(payload.sources) && payload.sources.length
            ? payload.sources
            : ['google', 'trustpilot', 'bookimed']
        },
        styles: {
          ...(payload.styles || {})
        },
        filters: {
          min_rating: payload.filters?.min_rating ?? 3,
          min_length: payload.filters?.min_length ?? 5
        }
      })
    },

    async loadWidgetSettings() {
      this.isLoading = true

      try {
        const response = await getWidgetSettings(this.clinic.id)
        const payload = response?.data?.data || response?.data || {}

        this.mapApiToForm(payload)
      } catch (error) {
        // keep defaults
      } finally {
        this.isLoading = false
      }
    },

    getPayload() {
      return {
        clinic_id: this.clinic.id,
        title: this.widgetData.widget.title,
        link: this.widgetData.widget.link,
        is_active: this.widgetData.widget.is_active,
        sources: this.widgetData.widget.sources,
        filters: {
          min_rating: this.filters.min_rating,
          min_length: this.filters.min_length
        },
        styles: {
          ...this.widgetData.styles
        }
      }
    },

    async recreateWidget() {
      const createResponse = await createWidget(this.clinic.id)
      const createdWidgetId =
        createResponse?.data?.data?.id ||
        createResponse?.data?.id ||
        createResponse?.id ||
        this.widgetId

      if (!createdWidgetId) {
        throw new Error('Widget ID was not returned')
      }

      this.widgetId = createdWidgetId
      return createdWidgetId
    },

    async fetchTrustWidgetData(widgetId, lang = 'en') {
      const apiLang = window.BookimedTrustWidget?.reviewLang
        ? window.BookimedTrustWidget.reviewLang(lang)
        : lang
      const url = `${TRUST_WIDGET_API_BASE}/${encodeURIComponent(apiLang)}/${encodeURIComponent(widgetId)}?_t=${Date.now()}`

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'Cache-Control': 'no-cache'
        },
        cache: 'no-store'
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      return response.json()
    },

    async saveAndGetPreviewData() {
      const data = this.getPayload()

      await saveWidgetSettings({
        clinicId: this.clinic.id,
        data
      })

      const widgetId = await this.recreateWidget()
      const trustData = await this.fetchTrustWidgetData(widgetId, this.lang)

      this.widgetId = trustData?.id || widgetId

      return trustData
    },

    buildEmbedCode(responseData) {
      const widgetId = responseData?.id || this.widgetId || ''
      const link = responseData?.settings?.link || 'https://us-uk.bookimed.com/'
      const lang = this.lang

      return `<div id="bookimed-trust-widget">
  <noscript>
    Powered by <a href="${link}">Bookimed</a>
  </noscript>

  <a
    href="${link}"
    class="bookimed-trust-link"
    target="_blank"
    rel="noopener"
    style="font-size:12px;color:#64748B;text-align:center;display:block;margin:8px 0;"
  >
    Powered by <span class="bookimed-trust-link__brand">Bookimed</span>
  </a>
</div>

<script
  src="https://bookimed-assets.s3.eu-central-1.amazonaws.com/partners/widget/trust-widget.js"
  data-widget-id="${widgetId}"
  data-widget-lang="${lang}"
  data-debug="true"
  async
><\/script>`
    },

    renderPreview() {
      if (!this.previewData) return
      if (!window.BookimedTrustWidget || typeof window.BookimedTrustWidget.render !== 'function') return

      const previewMount = this.$refs.widgetPreviewRoot
      if (!previewMount) return

      const root = previewMount.querySelector('#bookimed-trust-widget')
      if (!root) return

      root.className = 'widget-preview__widget-root'
      root.innerHTML = `
        <noscript>
          Powered by <a href="${this.previewLink}">Bookimed</a>
        </noscript>

        <a
          href="${this.previewLink}"
          class="bookimed-trust-link"
          target="_blank"
          rel="noopener"
          style="font-size:12px;color:#64748B;text-align:center;display:block;margin:8px 0;"
        >
          Powered by <span class="bookimed-trust-link__brand">Bookimed</span>
        </a>
      `

      window.BookimedTrustWidget.render(root, this.previewData, {
        lang: this.lang
      })
    },

    async remountAndRenderPreview() {
      this.previewVisible = false
      await this.$nextTick()

      this.previewRenderKey += 1
      this.previewVisible = true
      await this.$nextTick()

      this.renderPreview()
    },

    async handleParseWidget() {
      this.isParsing = true

      try {
        await Analytics.parseReviews({
          clinic_id: this.clinic.id,
          source: 'all'
        })

        this.$message.success('Widget parsing started successfully')
      } catch (error) {
        this.$message.error('Failed to parse widget')
      }
    },

    async handleSave() {
      this.isSaving = true

      try {
        const data = this.getPayload()

        await saveWidgetSettings({
          clinicId: this.clinic.id,
          data
        })

        await this.recreateWidget()

        this.$message.success('Settings saved and widget created successfully')
      } catch (error) {
        this.$message.error('Failed to save settings or create widget')
      } finally {
        this.isSaving = false
      }
    },

    async handlePreview() {
      this.isPreviewLoading = true

      try {
        const payload = await this.saveAndGetPreviewData()

        this.previewData = payload
        this.embedCode = this.buildEmbedCode(payload)

        await this.remountAndRenderPreview()

        this.$message.success('Widget preview loaded successfully')
      } catch (error) {
        this.$message.error('Failed to load widget preview')
      } finally {
        this.isPreviewLoading = false
      }
    },

    copyEmbedCode() {
      const text = this.embedCode || ''

      if (!text) return

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text)
          .then(() => {
            this.$message.success('Embed code copied')
          })
          .catch(() => {
            this.$message.error('Failed to copy embed code')
          })
        return
      }

      const textarea = document.createElement('textarea')
      textarea.value = text
      document.body.appendChild(textarea)
      textarea.select()

      try {
        document.execCommand('copy')
        this.$message.success('Embed code copied')
      } catch (error) {
        this.$message.error('Failed to copy embed code')
      }

      document.body.removeChild(textarea)
    }
  }
}
</script>

<style lang="scss">
.widget-top-actions {
  margin-top: 16px;
  display: flex;
  justify-content: flex-start;
}

.widget-form,
.widget-filters {
  margin-top: 20px;
}

.widget-filters__title {
  margin: 28px 0 12px;
  font-size: 20px;
  font-weight: 600;
}

.widget-actions {
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
  gap: 12px;

  &--left {
    justify-content: flex-start;
    margin-top: 12px;
  }
}

.widget-lang {
  margin-top: 24px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;

  .el-select {
    width: 260px;
  }

  &__label {
    font-weight: 600;
    font-size: 15px;
  }

  &__hint {
    flex-basis: 100%;
    font-size: 13px;
    color: #909399;
  }
}

.widget-snippet,
.widget-preview {
  margin-top: 24px;
  padding: 16px;
  border: 1px solid #dcdfe6;
  border-radius: 8px;
  background: #f8fafc;
}

.widget-preview__title {
  margin-bottom: 12px;
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.widget-preview__mount {
  overflow: auto;
}

.widget-preview__widget-root {
  min-height: 100px;
}

.widget-grid {
  display: grid;
  grid-template-columns: 220px 180px minmax(260px, 1fr) 1fr;
  border: 1px solid #dcdfe6;
  border-top: 0;

  &:first-child {
    border-top: 1px solid #dcdfe6;
  }

  > div {
    padding: 18px 16px;
    border-right: 1px solid #dcdfe6;
    display: flex;
    align-items: center;
    min-height: 72px;
    word-break: break-word;

    &:last-child {
      border-right: 0;
    }
  }

  &--header {
    background: #f5f7fa;
    font-weight: 600;

    > div {
      min-height: 54px;
    }
  }

  &__label {
    font-weight: 700;
    font-size: 16px;
  }

  &__inline {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;

    .el-input {
      flex: 1;
    }
  }

  &__color {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
  }

  .el-input,
  .el-select,
  .el-input-number {
    width: 100%;
  }
}

.widget-code {
  display: inline-block;
  padding: 4px 8px;
  background: #f5f7fa;
  border-radius: 6px;
  color: #606266;
  font-size: 13px;
  line-height: 1;
}

@media (max-width: 1200px) {
  .widget-grid {
    grid-template-columns: 180px 150px minmax(220px, 1fr) 1fr;
  }
}
</style>
